import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Field,
  Flex,
  Input,
  InputGroup,
  Portal,
} from '@chakra-ui/react';
import type { UserType } from '@customTypes/user';
import type { FormErrors } from '@customTypes/errors';
import { capitalizeFirstLetter } from '@utils/formats';
import { toast } from 'react-toastify';
import api from '@services/api';
import ErrorLabel from '@components/ErrorLabel';
import useGetUsersList from '@hooks/useGetUsersList';
import UserSelect from '@components/UserSelect';
import { datadogRum } from '@datadog/browser-rum';
import type { TeamType } from '../types';
import CustomDialogBackdrop from '@components/CustomDialogBackdrop';

interface Props {
  team: TeamType;
  handleClose: () => void;
  afterSubmit: () => void;
};

const UpdateDialog = ({ team, handleClose, afterSubmit }: Props) => {
  const {
    getUsers,
    users,
  } = useGetUsersList();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors | null>(null);
  const [selectedTeamLead, setSelectedTeamLead] = useState<UserType>({
    id: team.team_lead.id,
    name: team.team_lead.name,
    email: '',
    lockout_end: '',
    thumbnail_url: team.team_lead.thumbnail_url,
  });

  useEffect(() => {
    getUsers({});
  }, [getUsers]);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedTeamLead) {
      setErrors({ team_lead_id: ['Selecione um líder de equipe'] });
      return;
    }

    const formData = new FormData(event.currentTarget);
    const data = Object.fromEntries(formData.entries());

    setLoading(true);

    const params = {
      name: capitalizeFirstLetter(data.name as string),
      team_lead_id: selectedTeamLead.id,
    };

    try {
      const response = await api.patch(`/teams/${team.id}`, {
        ...params,
      });

      if (response?.status === 200) {
        toast.success('Equipe alterada com sucesso!');
        afterSubmit();
        handleClose();
      } else if (response?.data?.errors) {
        setErrors(response.data.errors);
      }
    } catch (error) {
      toast.error('Erro ao alterar a equipe. Verifique os dados e tente novamente.');
      datadogRum.addError(new Error('Fail to update team'), {
        error,
        params,
        teamId: team.id,
      });
    } finally {
      setLoading(false);
    };
  };

  const handleWithSelectTeamLead = async (user: UserType | null) => {
    if (!user) return;

    setSelectedTeamLead(user);
  };

  return (
    <Dialog.Root size="md" placement="center" open onOpenChange={handleClose} closeOnInteractOutside={false}>
      <Portal>
        <CustomDialogBackdrop />
        <Dialog.Positioner>
          <Dialog.Content bg="grayscale.fg">
            <Dialog.Header>
              <Dialog.Title>
                Alterar equipe
                {' '}
                {team.name}
              </Dialog.Title>
            </Dialog.Header>

            <form onSubmit={onSubmit}>
              <Dialog.Body>
                <Field.Root invalid={!!errors?.name}>
                  <Field.Label>Nome</Field.Label>
                  <InputGroup width="full" gap={0}>
                    <Input
                      name="name"
                      placeholder="Digite aqui..."
                      minLength={3}
                      variant="subtle"
                      defaultValue={team.name}
                      required
                    />
                  </InputGroup>
                  {errors?.name?.map((error, index) => (
                    <Field.ErrorText key={index}>{error}</Field.ErrorText>
                  ))}
                </Field.Root>

                <Box my={2}>
                  <UserSelect
                    label="Líder"
                    errors={errors}
                    users={users}
                    onSearch={async (text) => await getUsers({ name: text })}
                    onChange={handleWithSelectTeamLead}
                    value={selectedTeamLead}
                    showSelectedValue
                  />
                  {errors?.team_lead_id?.map((error, index) => (
                    <ErrorLabel key={index} text={error} />
                  ))}
                </Box>

                {errors?.general && (
                  <Flex direction="column" my={4}>
                    {errors.general.map((error, index) => (
                      <ErrorLabel key={index} text={error} />
                    ))}
                  </Flex>
                )}
              </Dialog.Body>

              <Dialog.Footer>
                <Dialog.ActionTrigger asChild>
                  <Button variant="outline" disabled={loading}>
                    Cancelar
                  </Button>
                </Dialog.ActionTrigger>
                <Button bg="green.700" color="white" type="submit" loading={loading}>
                  Salvar
                </Button>
              </Dialog.Footer>
            </form>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default UpdateDialog;
